import React, { useEffect, useState } from "react";
import Layout from "../../../layouts/default";
import { Button, Table, Spinner, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import JsonModal from "../EmailCampaignRunReport/JsonModel";
import SmartPagination from "../EmailCampaignRunReport/DynamicSmartPagination";
import { useDispatch, useSelector } from "react-redux";
import { getBulkSuppressionDeleteReports } from "../../../store/slices/suppressionList/suppressionList";

function BulkSuppressionDeleteReport() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
    });

    const [showModel, setShowModel] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);

    const { bulkDeleteReportList } = useSelector(
        (store) => store.suppressionEmailsSlice
    ) || {};

    const records = bulkDeleteReportList?.records || [];
    const totalPages = bulkDeleteReportList?.totalPages || 0;
    const loading = bulkDeleteReportList?.loading;

    const fetchReports = async (pageNo = page) => {
        dispatch(
            getBulkSuppressionDeleteReports({
                page: pageNo,
                limit: 10,
                filterData: filters,
            })
        );
    };

    useEffect(() => {
        fetchReports(page);
    }, [page]);

    const handleBack = () => navigate("/campaign/email-suppression-list");

    const handleFilter = (e) => {
        e.preventDefault();
        setPage(1);
        fetchReports(1);
    };

    const handleReset = () => {
        setFilters({ startDate: "", endDate: "" });
        setPage(1);
        fetchReports(1);
    };

    const showPayload = (record) => {
        setSelectedPayload(record);
        setShowModel(true);
    };

    const closeModal = () => {
        setSelectedPayload(null);
        setShowModel(false);
    };

    const today = DateTime.now().toISODate();

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                <Button variant="secondary" onClick={handleBack}>
                    ← Back
                </Button>
                <h3 className="m-0">Bulk Delete Report</h3>
            </div>

            {/* Filters */}
            <form
                onSubmit={handleFilter}
                className="mb-3 d-flex flex-wrap justify-content-between align-items-end gap-3"
            >
                <div className="d-flex flex-wrap gap-3">
                    <div className="d-flex flex-column">
                        <label className="form-label">Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            max={today}
                            value={filters.startDate}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, startDate: e.target.value }))
                            }
                        />
                    </div>

                    <div className="d-flex flex-column">
                        <label className="form-label">End Date</label>
                        <input
                            type="date"
                            className="form-control"
                            max={today}
                            value={filters.endDate}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, endDate: e.target.value }))
                            }
                        />
                    </div>

                    <div className="d-flex gap-2 align-items-end">
                        <Button type="submit" variant="primary">Filter</Button>
                        <Button type="button" variant="outline-secondary" onClick={handleReset}>
                            Reset
                        </Button>
                    </div>
                </div>
            </form>

            {/* Table */}
            <Table striped bordered hover responsive>
                <thead className="text-center align-middle">
                    <tr>
                        <th>S.No.</th>
                        <th>Status</th>
                        <th>Delete Emails Count</th>
                        <th>Created At</th>
                        <th>Deleted By</th>
                        <th>View</th>
                    </tr>
                </thead>

                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={6} className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-2">Loading...</div>
                            </td>
                        </tr>
                    ) : records.length > 0 ? (
                        records.map((record, index) => (
                            <tr key={record._id} className="align-middle text-center">
                                <td>{(page - 1) * 10 + index + 1}</td>
                                <td>
                                    <span
                                        className="badge rounded-pill px-3 py-2"
                                        style={{
                                            backgroundColor:
                                                record.status === "completed"
                                                    ? "#28a745"         // green
                                                    : record.status === "inProgress"
                                                        ? "#ffc107"     // yellow
                                                        : record.status === "processing"
                                                            ? "#0d6efd" // blue
                                                            : "#dc3545", // red (failed / unknown)
                                            color:
                                                record.status === "inProgress"
                                                    ? "#212529"
                                                    : "#fff",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {record.status === "completed"
                                            ? "Completed"
                                            : record.status === "inProgress"
                                                ? "In Progress"
                                                : record.status === "processing"
                                                    ? "Processing"
                                                    : "Failed"}
                                    </span>
                                </td>
                                <td>{record?.totalDeleted || record?.deletedCount || 0}</td>
                                <td>
                                    {record.createdAt
                                        ? DateTime.fromISO(record.createdAt).toFormat(
                                            "yyyy-MM-dd HH:mm:ss"
                                        )
                                        : "NA"}
                                </td>

                                <td>{record.userName || "NA"}</td>

                                <td>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => showPayload(record)}
                                    >
                                        View
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className="text-center py-4">
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {totalPages > 1 && (
                <SmartPagination
                    totalPages={totalPages}
                    currentPage={page}
                    onPageChange={setPage}
                />
            )}

            <JsonModal show={showModel} onClose={closeModal} payload={selectedPayload} />
        </Layout>
    );
}

export default BulkSuppressionDeleteReport;
